import { ref, computed, type Ref, type ComputedRef } from 'vue'
import { useToast } from '~/components/ui/toast'

export interface UseTTSReturn {
  isSupported: Ref<boolean>
  isPlaying: Ref<boolean>
  isPaused: Ref<boolean>
  isLoading: Ref<boolean>
  error: Ref<string | null>
  currentText: ComputedRef<string>
  speak: (text: string) => void
  pause: () => void
  stop: () => void
  getTextContent: (htmlContent: string) => string
}

export function useTTS(): UseTTSReturn {
  const isSupported = ref(false)
  const isPlaying = ref(false)
  const isPaused = ref(false)
  const isLoading = ref(false)
  const error = ref<string | null>(null)
  const { toast } = useToast()
  const { t } = useI18n()

  if (import.meta.client) {
    isSupported.value = typeof window !== 'undefined' && 'speechSynthesis' in window
  }

  const SPEED = 1.5

  let utterance: SpeechSynthesisUtterance | null = null
  let currentText = ''
  let intentionallyStopped = false

  // Check browser support
  const checkSupport = (): boolean => {
    if (typeof window === 'undefined' || !window.speechSynthesis) {
      error.value = t('tts.browser_not_supported')
      toast({
        title: t('error.title'),
        description: error.value,
        variant: 'destructive'
      })
      return false
    }
    return true
  }

  // Strip HTML tags and tables from content
  const getTextContent = (htmlContent: string): string => {
    if (typeof document === 'undefined') return htmlContent

    // Template content is inert: a detached div would make the browser load every <img> in it
    const temp = document.createElement('template')
    temp.innerHTML = htmlContent

    // Remove all table elements
    const tables = temp.content.querySelectorAll('table')
    tables.forEach((table) => table.remove())

    return temp.content.textContent || ''
  }

  // Select appropriate voice (prefer Russian or Kyrgyz)
  const selectVoice = (): SpeechSynthesisVoice | null => {
    const voices = window.speechSynthesis.getVoices()

    // Try to find Russian voice first
    let voice = voices.find((v) => v.lang.startsWith('ru'))

    // Fallback to Kyrgyz
    if (!voice) {
      voice = voices.find((v) => v.lang.startsWith('ky'))
    }

    // Fallback to any available voice
    if (!voice && voices.length > 0) {
      voice = voices[0]
    }

    return voice || null
  }

  // Start or resume speech
  const speak = (text: string): void => {
    if (!checkSupport()) return

    // Reset flag on new speech
    intentionallyStopped = false

    // If already paused with the same text, just resume
    if (isPaused.value && currentText === text && window.speechSynthesis.paused) {
      window.speechSynthesis.resume()
      isPaused.value = false
      isPlaying.value = true
      return
    }

    // Stop any current speech
    stop()

    currentText = text
    isLoading.value = true

    // Create new utterance
    utterance = new SpeechSynthesisUtterance(text)
    utterance.rate = SPEED
    utterance.pitch = 1.0
    utterance.volume = 1.0

    // Wait for voices to load
    const setVoiceAndSpeak = () => {
      if (!utterance) return

      const voice = selectVoice()
      if (voice) {
        utterance.voice = voice
        utterance.lang = voice.lang
      }

      // Event handlers
      utterance.onstart = () => {
        isLoading.value = false
        isPlaying.value = true
        isPaused.value = false
        error.value = null
      }

      utterance.onpause = () => {
        isPaused.value = true
        isPlaying.value = false
      }

      utterance.onresume = () => {
        isPaused.value = false
        isPlaying.value = true
      }

      utterance.onend = () => {
        isPlaying.value = false
        isPaused.value = false
        currentText = ''
      }

      utterance.onerror = (event) => {
        // Suppress error for intentional stop
        if (event.error === 'interrupted' && intentionallyStopped) {
          return
        }

        // Show error for all other cases using i18n
        error.value = t('tts.playback_error')
        toast({
          title: t('error.title'),
          description: error.value,
          variant: 'destructive'
        })
        stop()
      }

      try {
        window.speechSynthesis.speak(utterance)
      } catch {
        error.value = t('tts.failed_to_start')
        toast({
          title: t('error.title'),
          description: error.value,
          variant: 'destructive'
        })
        isLoading.value = false
      }
    }

    // Voices might not be loaded immediately
    const voices = window.speechSynthesis.getVoices()
    if (voices.length > 0) {
      setVoiceAndSpeak()
    } else {
      window.speechSynthesis.onvoiceschanged = () => {
        setVoiceAndSpeak()
      }
    }
  }

  // Pause speech
  const pause = (): void => {
    if (!checkSupport()) return

    if (isPlaying.value) {
      window.speechSynthesis.pause()
      isPaused.value = true
      isPlaying.value = false
    }
  }

  // Stop speech
  const stop = (): void => {
    // Mark as intentional stop
    intentionallyStopped = true

    if (typeof window !== 'undefined' && window.speechSynthesis) {
      window.speechSynthesis.onvoiceschanged = null
      window.speechSynthesis.cancel()
    }

    isPlaying.value = false
    isPaused.value = false
    isLoading.value = false
    currentText = ''
    utterance = null

    // Reset flag after cleanup
    setTimeout(() => {
      intentionallyStopped = false
    }, 100)
  }

  return {
    isSupported,
    isPlaying,
    isPaused,
    isLoading,
    error,
    currentText: computed(() => currentText),
    speak,
    pause,
    stop,
    getTextContent
  }
}
